import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { IDLE_CONTROL } from '../config-control';
import {
  BACKGROUND_CREATE_MODE,
  CONSULT_MODE,
  CREATE_MODE,
  NONE_BACKGROUND_MODE,
  NONE_MODE,
  NONE_SUB_MODE,
  UPDATE_MODE,
} from '../mode';
import {
  ALTERNATIVE_LOADING_STATUS,
  FAILURE_STATUS,
  IDLE_STATUS,
  LOADING_STATUS,
  PREPARING_ACTION_STATUS,
  SUCCESS_STATUS,
} from '../status';
import { fetchAll, fetchById, activateById, inactivateById } from './unidadeDeMedidaAPI';
import i18n from '../../i18n';

interface UnidadeDeMedidaState {
  status: string;
  error: any;
  response: {
    status: number | null;
    message: string | null;
    data: any;
  };
  list: any[];
  config: {
    control: string;
  };
  model: {
    id: string;
    code: string;
    description: string;
    active: boolean | null;
    insertionDate?: string | null;
  };
  mode: {
    main: string;
    background: string;
    sub: string;
  };
}

interface UnidadeDeMedida {
  id: string;
  code: string;
  description: string;
  active: boolean | null;
  insertionDate?: string | null;
  sigla?: string;
  vigencia?: {
    inicio: string;
    fim: string | null;
  };
}

const initialState: UnidadeDeMedidaState = {
  status: IDLE_STATUS,
  mode: {
    main: NONE_MODE,
    background: NONE_BACKGROUND_MODE,
    sub: NONE_SUB_MODE,
  },
  error: null,
  response: {
    status: null,
    message: null,
    data: null,
  },
  list: [],

  config: { control: IDLE_CONTROL },
  model: {
    id: '',
    code: '',
    active: null,
    description: '',
    insertionDate: null,
  },
};

const fetchAllAsync = createAsyncThunk('unidadeDeMedida/fetchAll', async () => {
  const { data } = await fetchAll();
  return { data };
});

const fetchByIdAsync = createAsyncThunk('unidadeDeMedida/fetchById', async (data) => {
  const response = await fetchById(data);
  return {
    response: {
      status: response.status,
      data: response.data,
    },
  };
});

const activateByIdAsync = createAsyncThunk('unidadeDeMedida/activateById', async (id) => {
  const { status, data } = await activateById(id);
  return { response: { status, data } };
});

const inactivateByIdAsync = createAsyncThunk('unidadeDeMedida/inactivateById', async (id) => {
  const { status, data } = await inactivateById(id);
  return { response: { status, data } };
});

const unidadeDeMedidaSlice = createSlice({
  name: 'unidadeDeMedida',
  initialState,
  reducers: {
    changeStatusTo: (state, action) => {
      state.status = action.payload.status;
    },
    loading: (state) => {
      state.status = LOADING_STATUS;
    },
    success: (state) => {
      state.status = SUCCESS_STATUS;
    },
    failure: (state) => {
      state.status = FAILURE_STATUS;
    },
    preparingAction: (state) => {
      state.status = PREPARING_ACTION_STATUS;
    },
    resetStatus: (state) => {
      state.status = IDLE_STATUS;
    },
    changeToCreateMode: (state) => {
      state.mode.main = CREATE_MODE;
    },
    changeToConsultMode: (state) => {
      state.mode.main = CONSULT_MODE;
    },
    changeToUpdateMode: (state) => {
      state.mode.main = UPDATE_MODE;
    },
    resetMode: (state) => {
      state.mode.main = NONE_MODE;
    },
    changeToBackgroundCreateMode: (state) => {
      state.mode.background = BACKGROUND_CREATE_MODE;
    },
    resetBackgroundMode: (state) => {
      state.mode.background = NONE_BACKGROUND_MODE;
    },
    setModel: (state, action) => {
      state.model = {
        ...action.payload,
      };
    },
    addToList: (state, action) => {
      state.list = [...state.list, action.payload.data];
    },
    updateOnList: (state, action) => {
      state.list = state.list.map((current) => {
        if (current.id === action.payload.data?.id) {
          return action.payload.data;
        }
        return current;
      });
    },
    setResponse: (state, action) => {
      state.response = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
    clearResponse: (state) => {
      state.response = {
        status: null,
        message: null,
        data: null,
      };
    },
    resetModel: (state) => {
      state.model = initialState.model;
    },
    setConfig: (state, action) => {
      state.config = action.payload;
    },
    changeControlTo: (state, action) => {
      state.config.control = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAllAsync.pending, (state) => {
        state.status = ALTERNATIVE_LOADING_STATUS;
      })
      .addCase(fetchAllAsync.fulfilled, (state, action) => {
        state.status = IDLE_STATUS;
        state.list = action.payload.data;
      })
      .addCase(fetchAllAsync.rejected, (state, action) => {
        state.status = FAILURE_STATUS;
        state.error = {
          ...action.error,
          message: i18n.t('com.muralis.qcx.erro.erroCarregarListaUnidadeMedida', { erro: action.error.message }),
        };
      })
      .addCase(fetchByIdAsync.pending, (state) => {
        state.status = LOADING_STATUS;
      })
      .addCase(fetchByIdAsync.fulfilled, (state, action) => {
        if (action.payload.response.status === 200) {
          state.model = action.payload.response.data;
          state.status = SUCCESS_STATUS;
        }
      })
      .addCase(fetchByIdAsync.rejected, (state, action) => {
        state.status = FAILURE_STATUS;
        state.error = {
          ...action.error,
          message: i18n.t('com.muralis.qcx.erro.erroCarregarDadosUnidadeMedida', { erro: action.error.message }),
        };
      })
      .addCase(activateByIdAsync.pending, (state) => {
        state.status = LOADING_STATUS;
      })
      .addCase(activateByIdAsync.fulfilled, (state, action) => {
        state.status = SUCCESS_STATUS;
        state.response.status = action.payload.response.status;
        state.response.data = action.payload.response.data;

        const { description } = action.payload.response.data;
        state.response.message = i18n.t('com.muralis.qcx.mensagem.unidadeMedidaAtivada', {
          unidadeMedida: description,
        });

        if (state.response.status === 200) {
          state.list = state.list.map((current) =>
            current.id === action.payload.response.data.id ? action.payload.response.data : current
          );
        }
      })
      .addCase(activateByIdAsync.rejected, (state, action) => {
        state.status = FAILURE_STATUS;
        state.error = {
          ...action.error,
          message: i18n.t('com.muralis.qcx.erro.erroAtivarUnidadeMedida', { erro: action.error.message }),
        };
      })
      .addCase(inactivateByIdAsync.pending, (state) => {
        state.status = LOADING_STATUS;
      })
      .addCase(inactivateByIdAsync.fulfilled, (state, action) => {
        state.status = SUCCESS_STATUS;
        state.response.status = action.payload.response.status;
        state.response.data = action.payload.response.data;

        const { description } = action.payload.response.data;
        state.response.message = i18n.t('com.muralis.qcx.mensagem.unidadeMedidaInativada', {
          unidadeMedida: description,
        });

        if (state.response.status === 200) {
          state.list = state.list.map((current) =>
            current.id === action.payload.response.data.id ? action.payload.response.data : current
          );
        }
      })
      .addCase(inactivateByIdAsync.rejected, (state, action) => {
        state.status = FAILURE_STATUS;
        state.error = {
          ...action.error,
          message: i18n.t('com.muralis.qcx.erro.erroInativarUnidadeMedida', { erro: action.error.message }),
        };
      });
  },
});

const {
  changeStatusTo,
  loading,
  success,
  failure,
  changeToBackgroundCreateMode,
  changeToConsultMode,
  changeToCreateMode,
  changeToUpdateMode,
  preparingAction,
  resetBackgroundMode,
  resetMode,
  setModel,
  resetStatus,
  addToList,
  updateOnList,
  setResponse,
  clearResponse,
  changeControlTo,
  setConfig,
  setError,
  resetModel,
} = unidadeDeMedidaSlice.actions;

const selectConfigControl = (state: any) => state.unidadeMedida.config.control;
const selectUnidadeDeMedida = (state: any) => state.unidadeMedida.model as UnidadeDeMedida;
const selectUnidadesdeMedida = (state: any) => state.unidadeMedida.list as UnidadeDeMedida[];
const selectStatus = (state: any) => state.unidadeMedida.status;
const selectError = (state: any) => state.unidadeMedida.error;
const selectResponse = (state: any) => state.unidadeMedida.response;
const selectMode = (state: any) => state.unidadeMedida.mode.main;
const selectBackgroundMode = (state: any) => state.unidadeMedida.mode.background;

const unidadeDeMedidaActions = {
  ...unidadeDeMedidaSlice.actions,
  selectConfigControl,
  selectUnidadesdeMedida,
  selectStatus,
  selectError,
  selectResponse,
  selectUnidadeDeMedida,
  fetchAllAsync,
};

export {
  unidadeDeMedidaSlice,
  changeStatusTo,
  loading,
  success,
  failure,
  changeToBackgroundCreateMode,
  changeToConsultMode,
  changeToCreateMode,
  changeToUpdateMode,
  preparingAction,
  resetBackgroundMode,
  resetMode,
  setModel,
  resetStatus,
  addToList,
  updateOnList,
  setResponse,
  clearResponse,
  setError,
  resetModel,
  setConfig,
  changeControlTo,
  fetchAllAsync,
  fetchByIdAsync,
  activateByIdAsync,
  inactivateByIdAsync,
  selectUnidadesdeMedida,
  selectUnidadeDeMedida,
  selectStatus,
  selectError,
  selectResponse,
  selectConfigControl,
  selectMode,
  selectBackgroundMode,
  unidadeDeMedidaActions,
};

export default unidadeDeMedidaSlice.reducer;
